import React, { useMemo, useEffect, useCallback, useContext, useRef, useState } from 'react';
import { Box, Alert, Grid, Tabs, Tab } from '@mui/material';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { usePrompt } from '../../../../AppPrompt';
import { useMonitoringReportShared } from '../../context/MonitoringReportContext';
import ProjectDrawSummary from '../../../../project/ProjectDrawSummary';
import ProjectBudget from '../../../../project/ProjectBudget';
import ProjectTable from '../../../../project/ProjectTable';
import ProjectPreviousFundingsTable from '../../../../project/ProjectPreviousFundingsTable';
import TabPanel from '../../../../general/TabPanel';
import { useQuery } from 'react-query';
import keys from 'common/react-query/data-keys';
import { getDivisions } from '../../../../glossary/glossary.api';
import { ONE_DAY_MS } from 'common/constants/general';
import { CreateOrUpdateProjectDrawDTO } from '../../../../project/project-draws/project-draws.type';
import { BudgetSummaryDTO, ProjectData, ProjectLineItemsAndSummary, ProjectPreviousFundingDTO } from '../../../../project/project.type';
import { sumPreviousFundings } from '../../../../project/project.util';
import { DrawContext } from '../../../magic-report/magic-report.type';
import { ApplicationCommentsSection } from '../../../magic-report/sections/ApplicationCommentsSection';
import { ReductionCommentsSection } from '../../../magic-report/sections/ReductionCommentsSection';
import { useReductionGateStatus } from '../../hooks/useReductionGateStatus';
import { calculateBudgetSummaryFromLineItems } from './budget-summary.util';
import { useReviewBudgetDrawTab } from './ReviewBudgetDrawTabContext';

/** Anchor ids are suffixed with the draw id so each draw's tab has its own scroll targets. */
const drawAnchorId = (base: string, drawId: string) => `${base}__${drawId}`;

interface ReviewBudgetDrawPanelProps {
  draw: DrawContext;
  isMagicReport: boolean;
  magicReportId?: string;
  divisions: any[];
  isDirty: boolean;
  updateDraw: (drawId: string, patch: Partial<DrawContext>) => void;
  highlightedLineItemIds: string[];
  onToggleLineItemHighlight?: (lineItemId: string) => void;
}

/**
 * One draw's G702/3 review: draw summary, budget, previous fundings, line items, and (for magic
 * reports) that draw's own Application and Reduction Comments.
 */
const ReviewBudgetDrawPanel: React.FC<ReviewBudgetDrawPanelProps> = ({
  draw,
  isMagicReport,
  magicReportId,
  divisions,
  isDirty,
  updateDraw,
  highlightedLineItemIds,
  onToggleLineItemHighlight,
}) => {
  const { reportState } = useMonitoringReportShared() as any;
  const { projectSummary } = reportState;
  const { drawId, drawSummary, budgetSummary, lineItems } = draw;

  const previousFundingsTotal = useMemo(
    () => sumPreviousFundings(drawSummary?.previousFundings, 'amountFunded'),
    [drawSummary?.previousFundings],
  );

  const handleDrawEditChange = useCallback(
    (field: string, value: any, target: 'drawSummary' | 'budgetSummary') => {
      if (target === 'drawSummary') {
        updateDraw(drawId, { drawSummary: { ...drawSummary, [field]: value } });
      } else if (target === 'budgetSummary') {
        updateDraw(drawId, { budgetSummary: { ...budgetSummary, [field]: value } as BudgetSummaryDTO });
      }
    },
    [drawId, drawSummary, budgetSummary, updateDraw],
  );

  /**
   * Updates this draw's previousFundings and, only when this edit actually changed the "Funded by
   * Lender" total, syncs lessPreviousFundings to match — so Current Payment Due / Total Amount
   * Recommended for Funding / the exported budget all stay consistent. Comparing against the
   * previous total (not the persisted budget field) means edits to unrelated columns (Comment,
   * Application Date, etc.) never trigger a sync.
   */
  const handlePreviousFundingsChange = useCallback(
    (updated: ProjectPreviousFundingDTO[]) => {
      const previousTotal = sumPreviousFundings(drawSummary?.previousFundings, 'amountFunded');
      const updatedTotal = sumPreviousFundings(updated, 'amountFunded');
      const patch: Partial<DrawContext> = { drawSummary: { ...drawSummary, previousFundings: updated } };
      if (Math.abs(previousTotal - updatedTotal) >= 0.01) {
        patch.budgetSummary = { ...budgetSummary, lessPreviousFundings: updatedTotal } as BudgetSummaryDTO;
      }
      updateDraw(drawId, patch);
    },
    [drawId, drawSummary, budgetSummary, updateDraw],
  );

  const calculatedBudgetSummary = useMemo(() => calculateBudgetSummaryFromLineItems(lineItems, budgetSummary), [lineItems, budgetSummary]);

  // Sync derived budget fields back into this draw so the PDF export (which reads raw state)
  // always gets the recalculated values rather than stale pre-adjustment ones.
  useEffect(() => {
    if (!calculatedBudgetSummary) return;

    const derivedFields: Array<keyof BudgetSummaryDTO> = [
      'totalAmountReccForFunding',
      'difference',
      'currentPaymentDue',
      'totalRetainageHeld',
      'totalRetainageHeldPercentage',
      'totalContractBudgetPerComplete',
      'originalContractBudgetSum',
      'balanceToCompletePlusRetainage',
      'contractBudgetSumToDate',
      'totalCompletedAndStored',
      'totalEarnedLessRetainage',
      'reallocationsChangeOrders',
    ];

    const hasChanges = derivedFields.some(f => (budgetSummary as any)?.[f] !== (calculatedBudgetSummary as any)[f]);
    if (!hasChanges) return;

    const updates = derivedFields.reduce((acc, f) => {
      (acc as any)[f] = (calculatedBudgetSummary as any)[f];
      return acc;
    }, {} as Partial<BudgetSummaryDTO>);

    updateDraw(drawId, { budgetSummary: { ...budgetSummary, ...updates } as BudgetSummaryDTO });
  }, [calculatedBudgetSummary]); // intentionally omits budgetSummary/updateDraw (would loop)

  const handleDetailedDrawsChange = (newDetailedDraws: ProjectLineItemsAndSummary[]) => {
    const updated = newDetailedDraws.find(d => d.projectDrawSummary.id === drawId);
    if (!updated) return;
    updateDraw(drawId, {
      ...(updated.lineItems && { lineItems: updated.lineItems }),
      subtotals: updated.subtotals,
      pendingSubtotalReanchors: updated.pendingSubtotalReanchors,
    });
  };

  const handleUpdateProject = (updatedProject: Partial<ProjectData>) => {
    if (!updatedProject.projectDraws) return;
    const updated = updatedProject.projectDraws.find((d: ProjectLineItemsAndSummary) => d.projectDrawSummary.id === drawId);
    if (updated) {
      updateDraw(drawId, {
        drawSummary: updated.projectDrawSummary,
        budgetSummary: updated.budgetSummary,
        lineItems: updated.lineItems,
        subtotals: updated.subtotals,
        pendingSubtotalReanchors: updated.pendingSubtotalReanchors,
      });
    }
  };

  const { requiresManualComment, hasManualComment } = useReductionGateStatus(reportState, isMagicReport, drawId);
  const showCommentRequiredAlert = requiresManualComment && !hasManualComment;

  return (
    <Box>
      <Box id={drawAnchorId('review-budget-g702-3', drawId)}>
        {drawSummary && (
          <ProjectDrawSummary
            drawSummary={drawSummary}
            readonly={false}
            isEditMode={true}
            onEditChange={handleDrawEditChange}
            errorFieldNames={[]}
          />
        )}

        {/* Mirrors the Excel G702/3 sheet: previous fundings on the left, the single-column budget summary on the right. */}
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
            <ProjectPreviousFundingsTable
              fullHeight
              previousFundings={drawSummary?.previousFundings}
              isEditMode={true}
              onChange={handlePreviousFundingsChange}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
            <ProjectBudget
              singleColumn
              fullHeight
              budgetSummary={calculatedBudgetSummary}
              drawBudget={calculatedBudgetSummary}
              errorFieldNames={[]}
              isEditMode={true}
              onEditChange={handleDrawEditChange}
              previousFundingsTotal={previousFundingsTotal}
            />
          </Grid>
        </Grid>

        <ProjectTable
          projectId={projectSummary?.projectId || ''}
          projectNumber={projectSummary?.projectNumber}
          projectName={projectSummary?.projectTitle}
          projectDrawId={drawId}
          projectDrawName={drawSummary?.name || ''}
          drawInputRetainagePercentage={(drawSummary?.inputRetainagePercentage || 0) / 100}
          onDrawInputRetainagePercentageChange={handleDrawEditChange}
          inputRetainagePercentageHasError={false}
          lineItems={lineItems || []}
          openAutoAssignDialog={() => undefined}
          predictingDivisions={false}
          divisionsData={divisions || []}
          isLoading={false}
          updateProject={handleUpdateProject}
          detailedDraws={
            drawSummary && lineItems
              ? [
                  {
                    projectDrawSummary: drawSummary,
                    lineItems: lineItems,
                    budgetSummary: calculatedBudgetSummary,
                    subtotals: draw.subtotals,
                    pendingSubtotalReanchors: draw.pendingSubtotalReanchors,
                  },
                ]
              : []
          }
          setDetailedDraws={handleDetailedDrawsChange as any}
          readonly={false}
          payappDrawId={''}
          payappMergedDraw={undefined}
          hasCalledHandlePayappSave={false}
          setHasCalledHandlePayappSave={() => undefined}
          handleEditDrawSubmit={async (_vals: CreateOrUpdateProjectDrawDTO) => undefined}
          isProjectDirty={isDirty}
          isMonitoringReport={true}
          highlightedLineItemIds={highlightedLineItemIds}
          onToggleLineItemHighlight={onToggleLineItemHighlight}
        />
      </Box>

      {isMagicReport && magicReportId && (
        <>
          <Box id={drawAnchorId('review-budget-application-comments', drawId)} sx={{ mt: 4 }}>
            <ApplicationCommentsSection magicReportId={magicReportId} drawId={drawId} />
          </Box>
          {showCommentRequiredAlert && (
            <Alert severity='error' sx={{ mt: 2 }}>
              A negative difference was detected in the budget summary. Please enter a manual reduction comment in the Reduction Comments
              section below before proceeding.
            </Alert>
          )}
          <Box id={drawAnchorId('review-budget-reduction-comments', drawId)} sx={{ mt: 4 }}>
            <ReductionCommentsSection magicReportId={magicReportId} drawId={drawId} />
          </Box>
        </>
      )}
    </Box>
  );
};

const ReviewBudgetStep: React.FC = () => {
  const { reportState, setReportState, isDirty, setIsDirty, isDirtyRef, reportType, saveReport } = useMonitoringReportShared() as any;
  const { getConfirmation } = usePrompt();
  const { navigator } = useContext(UNSAFE_NavigationContext);

  const saveReportRef = useRef(saveReport);
  saveReportRef.current = saveReport;
  useEffect(() => () => void saveReportRef.current({ silent: true }), []);

  const reportId: string | undefined = reportState.reportId;
  useEffect(() => {
    if (!reportId) return;
    const history = navigator as any;
    const { push, replace } = history;
    const guard =
      (navigate: (...args: any[]) => void) =>
      async (...args: any[]) => {
        const to = args[0];
        const pathname: string = (typeof to === 'string' ? to : to?.pathname) ?? '';
        if (!isDirtyRef.current || pathname.includes(`/${reportId}`)) return navigate(...args);

        const shouldSave = await getConfirmation({
          heading: 'Unsaved Changes',
          description: 'You have unsaved changes in Review Budget. Save them to the project before leaving?',
          confirmButton: { label: 'Save & Leave', color: 'primary' },
          cancelButton: { label: 'Stay', color: 'secondary' },
        });
        if (!shouldSave) return;

        await saveReportRef.current({ silent: true });
        if (isDirtyRef.current) return;
        navigate(...args);
      };
    history.push = guard(push);
    history.replace = guard(replace);
    return () => {
      history.push = push;
      history.replace = replace;
    };
  }, [navigator, reportId, isDirtyRef, getConfirmation]);

  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const isMagicReport = reportType === 'magic';
  const magicReportId: string | undefined = isMagicReport
    ? ((reportState.reportId || reportState.magicReportId) as string | undefined)
    : undefined;

  /**
   * The draws this report covers. Reports opened before multi-draw (and non-magic reports) have no
   * `draws` list, so the single drawSummary/budgetSummary/lineItems trio becomes a one-draw list
   * and the step renders exactly as it always did.
   */
  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    if (!reportState.drawSummary) return [];
    return [
      {
        drawId: reportState.drawSummary.id,
        drawSummary: reportState.drawSummary,
        budgetSummary: reportState.budgetSummary,
        lineItems: reportState.lineItems,
        subtotals: reportState.subtotals,
      },
    ];
  }, [reportState.draws, reportState.drawSummary, reportState.budgetSummary, reportState.lineItems, reportState.subtotals]);

  // The wizard stepper drives the tab when a draw-scoped substep is clicked; local state is the
  // fallback (no provider, or no draw selected yet).
  const drawTab = useReviewBudgetDrawTab();
  const [localTabIndex, setLocalTabIndex] = useState(0);
  const selectedIndexFromContext = drawTab?.activeDrawId ? draws.findIndex(draw => draw.drawId === drawTab.activeDrawId) : -1;
  const tabIndex = selectedIndexFromContext >= 0 ? selectedIndexFromContext : localTabIndex;

  const selectTab = useCallback(
    (index: number) => {
      setLocalTabIndex(index);
      drawTab?.setActiveDrawId(draws[index]?.drawId ?? null);
    },
    [drawTab, draws],
  );

  useEffect(() => {
    // Keep the active tab valid if the covered draws change.
    if (localTabIndex > draws.length - 1) setLocalTabIndex(0);
  }, [draws.length, localTabIndex]);

  /**
   * The single place draw edits are written. Updates the draw's entry in `draws` and, for the
   * primary draw, refreshes the deprecated top-level mirrors that sections not yet migrated to
   * multi-draw still read. Remove the mirroring once those all read `draws`.
   */
  const updateDraw = useCallback(
    (drawId: string, patch: Partial<DrawContext>) => {
      setIsDirty(true);
      setReportState((prev: any) => {
        const prevDraws: DrawContext[] = prev.draws?.length
          ? prev.draws
          : prev.drawSummary
            ? [
                {
                  drawId: prev.drawSummary.id,
                  drawSummary: prev.drawSummary,
                  budgetSummary: prev.budgetSummary,
                  lineItems: prev.lineItems,
                  subtotals: prev.subtotals,
                },
              ]
            : [];

        const nextDraws = prevDraws.map(draw => (draw.drawId === drawId ? { ...draw, ...patch } : draw));

        // Only `draws` is written. The primary-draw mirrors (drawSummary/budgetSummary/lineItems)
        // are derived from it by the context, so writing them here would be redundant.
        return { ...prev, draws: nextDraws };
      });
    },
    [setIsDirty, setReportState],
  );

  const highlightedLineItemIds: string[] = isMagicReport ? reportState.highlightedLineItemIds || [] : [];

  const handleToggleLineItemHighlight = useCallback(
    (lineItemId: string) => {
      if (!isMagicReport) return;
      setIsDirty(true);
      setReportState((prev: any) => {
        const current: string[] = prev.highlightedLineItemIds || [];
        const next = current.includes(lineItemId) ? current.filter(id => id !== lineItemId) : [...current, lineItemId];
        return { ...prev, highlightedLineItemIds: next };
      });
    },
    [isMagicReport, setIsDirty, setReportState],
  );

  const { data: divisions } = useQuery(keys.divisions, async () => await getDivisions(), { staleTime: ONE_DAY_MS });

  const { offendingDrawNames } = useReductionGateStatus(reportState, isMagicReport);

  const showTabs = draws.length > 1;

  return (
    <Box>
      {showTabs && offendingDrawNames.length > 0 && (
        <Alert severity='error' sx={{ mb: 2 }}>
          A manual reduction comment is still required for: {offendingDrawNames.join(', ')}.
        </Alert>
      )}

      {showTabs && (
        <Tabs
          value={tabIndex}
          onChange={(_event, newValue) => selectTab(newValue)}
          sx={{ '.MuiTab-root': { fontWeight: 'bold', '&:hover': { backgroundColor: '#eaf5f7' } } }}
        >
          {draws.map(draw => (
            <Tab key={draw.drawId} label={draw.drawSummary?.name || 'Draw'} />
          ))}
        </Tabs>
      )}

      {draws.map((draw, index) => (
        <TabPanel key={draw.drawId} value={showTabs ? tabIndex : 0} index={showTabs ? index : 0}>
          {(!showTabs || tabIndex === index) && (
            <ReviewBudgetDrawPanel
              draw={draw}
              isMagicReport={isMagicReport}
              magicReportId={magicReportId}
              divisions={divisions || []}
              isDirty={isDirty}
              updateDraw={updateDraw}
              highlightedLineItemIds={highlightedLineItemIds}
              onToggleLineItemHighlight={isMagicReport ? handleToggleLineItemHighlight : undefined}
            />
          )}
        </TabPanel>
      ))}
    </Box>
  );
};

export default ReviewBudgetStep;
